import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { DEFAULT_PARAMS } from '@/lib/operations';
import { generateGcode, parseGcode as apiParseGcode } from '@/lib/api';
import Topbar from '@/components/wizard/Topbar';
import OperationSidebar from '@/components/wizard/OperationSidebar';
import ParameterPanel from '@/components/wizard/ParameterPanel';
import Viewport3D from '@/components/wizard/Viewport3D';
import GCodePanel from '@/components/wizard/GCodePanel';
import ValidationPanel from '@/components/wizard/ValidationPanel';
import ToolsDialog from '@/components/wizard/ToolsDialog';
import ProjectsDialog from '@/components/wizard/ProjectsDialog';
import FeedSpeedDialog from '@/components/wizard/FeedSpeedDialog';
import DxfDialog from '@/components/wizard/DxfDialog';
import SettingsDialog from '@/components/wizard/SettingsDialog';
import ImportGcodeDialog from '@/components/wizard/ImportGcodeDialog';

const deepCopy = (o) => JSON.parse(JSON.stringify(o));

export default function Workspace() {
  const [op, setOp] = useState('pocket');
  const [paramsMap, setParamsMap] = useState(() => deepCopy(DEFAULT_PARAMS));
  const [gcode, setGcode] = useState('');
  const [toolpath, setToolpath] = useState([]);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [projectName, setProjectName] = useState('Uj_projekt');
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // Dialogs
  const [dlgTools, setDlgTools] = useState(false);
  const [dlgProjects, setDlgProjects] = useState(false);
  const [dlgFS, setDlgFS] = useState(false);
  const [dlgDxf, setDlgDxf] = useState(false);
  const [dlgSettings, setDlgSettings] = useState(false);
  const [dlgImport, setDlgImport] = useState(false);

  const params = paramsMap[op];

  const setParams = useCallback((newParams) => {
    setParamsMap(m => ({ ...m, [op]: newParams }));
  }, [op]);

  // Global post settings (share across ops when they change)
  const globalPost = params?.post;
  const setGlobalPost = useCallback((post) => {
    setParamsMap(m => {
      const nm = { ...m };
      Object.keys(nm).forEach(k => { nm[k] = { ...nm[k], post: { ...post } }; });
      return nm;
    });
  }, []);

  const handleGenerate = async () => {
    setBusy(true);
    try {
      // ensure program_name uses projectName
      const p = deepCopy(params);
      p.post = { ...p.post, program_name: (projectName || 'PROGRAM').toUpperCase().replace(/[^A-Z0-9_]/g,'_').slice(0,32) };
      const res = await generateGcode(op, p);
      setGcode(res.gcode);
      setToolpath(res.toolpath || []);
      setValidation(res.validation);
      if (res.validation?.ok) toast.success('G-kod sikeresen generalva!');
      else toast.error('G-kod hibakkal generalodott, ellenorizze a validaciot');
    } catch (e) {
      console.error(e);
      toast.error(`Hiba a generalas soran: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleImportGcode = async (text) => {
    setGcode(text);
    try {
      const res = await apiParseGcode(text);
      setToolpath(res.toolpath || []);
      toast.success(`G-kod importalva - ${res.meta?.segments || 0} szegmens`);
    } catch (e) {
      toast.error('G-kod parsolasi hiba');
    }
  };

  const applyDxfContour = (points) => {
    // Set as contour params and switch to contour op
    setOp('contour');
    setParamsMap(m => ({
      ...m,
      contour: { ...m.contour, points, closed: points.length > 2 && (points[0][0]===points[points.length-1][0] && points[0][1]===points[points.length-1][1]) },
    }));
    toast.success(`DXF kontur betoltve - ${points.length} pont`);
  };

  const applyFeedSpeed = (fs) => {
    const key = op;
    setParamsMap(m => {
      const cur = { ...m[key] };
      if ('feed_xy' in cur) cur.feed_xy = fs.feed_xy_mm_min;
      if ('feed_z' in cur) cur.feed_z = fs.feed_z_mm_min;
      if ('rpm' in cur) cur.rpm = fs.rpm;
      return { ...m, [key]: cur };
    });
    toast.success('Feed/speed atveve az aktualis muveletre');
  };

  const loadProject = (p) => {
    setCurrentProjectId(p.id);
    setProjectName(p.name);
    setOp(p.operation);
    setParamsMap(m => ({ ...m, [p.operation]: p.params }));
    if (p.gcode) setGcode(p.gcode);
  };

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      <Topbar
        projectName={projectName}
        setProjectName={setProjectName}
        currentProjectId={currentProjectId}
        gcode={gcode}
        operation={op}
        params={params}
        globalPost={globalPost}
        setGlobalPost={setGlobalPost}
        onOpenTools={() => setDlgTools(true)}
        onOpenProjects={() => setDlgProjects(true)}
        onOpenFS={() => setDlgFS(true)}
        onOpenDxf={() => setDlgDxf(true)}
        onOpenSettings={() => setDlgSettings(true)}
        onOpenImport={() => setDlgImport(true)}
        onSavedProject={(saved) => { setCurrentProjectId(saved.id); toast.success('Projekt mentve'); }}
      />
      <div className="flex-1 min-h-0 grid grid-cols-[340px_minmax(0,1fr)_500px] gap-3 p-3">
        {/* Left panel */}
        <div className="panel flex flex-col min-h-0">
          <OperationSidebar op={op} setOp={setOp} />
          <ParameterPanel
            op={op}
            params={params}
            setParams={setParams}
            onGenerate={handleGenerate}
            busy={busy}
          />
        </div>
        {/* Center 3D */}
        <div className="panel flex flex-col min-h-0">
          <Viewport3D toolpath={toolpath} params={params} op={op} />
        </div>
        {/* Right */}
        <div className="flex flex-col gap-3 min-h-0">
          <div className="panel flex-1 min-h-0 flex flex-col">
            <GCodePanel gcode={gcode} setGcode={setGcode} onImportRequest={() => setDlgImport(true)} onImportedText={handleImportGcode} projectName={projectName} />
          </div>
          <div className="panel">
            <ValidationPanel validation={validation} />
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <ToolsDialog open={dlgTools} onOpenChange={setDlgTools} />
      <ProjectsDialog
        open={dlgProjects}
        onOpenChange={setDlgProjects}
        currentSnapshot={{ name: projectName, operation: op, params, gcode }}
        onLoad={(p) => { loadProject(p); setDlgProjects(false); }}
      />
      <FeedSpeedDialog open={dlgFS} onOpenChange={setDlgFS} onApply={(fs) => { applyFeedSpeed(fs); setDlgFS(false); }} />
      <DxfDialog open={dlgDxf} onOpenChange={setDlgDxf} onApply={(pts) => { applyDxfContour(pts); setDlgDxf(false); }} />
      <SettingsDialog open={dlgSettings} onOpenChange={setDlgSettings} post={globalPost} setPost={setGlobalPost} />
      <ImportGcodeDialog open={dlgImport} onOpenChange={setDlgImport} onImport={(text) => { handleImportGcode(text); setDlgImport(false); }} />
    </div>
  );
}
